interface LogLevel {
  ERROR: 0;
  WARN: 1;
  INFO: 2;
  DEBUG: 3;
}

const LOG_LEVELS: LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// ✅ Buffer circulaire pour capturer les derniers logs avant un crash
export interface LogEntry {
  level: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
  message: string;
  timestamp: number;
  sessionId: string; // ✅ Corrélation multi-événements pour un même onglet
  data?: string; // JSON stringifié et sanitizé (max 200 chars)
}

const LOG_BUFFER_SIZE = 50;
const logBuffer: LogEntry[] = [];

// ✅ Session ID — généré une fois au boot du module, tient en RAM uniquement.
// Reset à chaque reload (1 session = 1 onglet). Permet de regrouper les logs
// d'une même session dans les rapports d'erreur (app_error_reports.metadata.recentLogs).
function generateSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().slice(0, 8);
    }
  } catch { /* fallback */ }
  return Math.random().toString(36).slice(2, 10);
}
const SESSION_ID = generateSessionId();

/**
 * Détecte précisément l'environnement d'exécution
 * - development: localhost
 * - lovable-preview: *-preview--*.lovable.app
 * - lovable-published: *.lovable.app ou *.lovableproject.com
 * - production: domaine custom (réel)
 */
function detectEnvironment(): 'lovable-preview' | 'lovable-published' | 'production' | 'development' {
  if (typeof window === 'undefined') return 'production';
  
  const hostname = window.location.hostname;
  
  // Développement local
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  // Preview Lovable (id-preview--xxx.lovable.app)
  if (hostname.includes('-preview--') && hostname.endsWith('.lovable.app')) {
    return 'lovable-preview';
  }
  
  // App publiée Lovable (xxx.lovable.app sans preview) ou lovableproject.com
  if (hostname.endsWith('.lovable.app') || hostname.endsWith('.lovableproject.com')) {
    return 'lovable-published';
  }
  
  // Production réelle (domaine custom)
  return 'production';
}

class Logger {
  private currentLevel: number;
  private environment: ReturnType<typeof detectEnvironment>;

  constructor() {
    this.environment = detectEnvironment();
    
    // ✅ FIX: forceDebug via localStorage pour diagnostic en production
    const forceDebug = typeof localStorage !== 'undefined' && localStorage.getItem('debug') === 'true';
    
    // Lovable preview/published → DEBUG pour diagnostic
    // Production réelle (domaine custom) → ERROR uniquement (données protégées par sanitizeData)
    this.currentLevel = (this.environment === 'production' && !forceDebug) 
      ? LOG_LEVELS.ERROR 
      : LOG_LEVELS.DEBUG;
  }
  
  // ✅ Permettre de changer le niveau de log à runtime
  setLevel(level: keyof LogLevel): void {
    this.currentLevel = LOG_LEVELS[level];
    console.log(`[Logger] Level set to ${level}`);
  }
  
  // ✅ Activer/désactiver le mode debug persistant
  enableDebug(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('debug', 'true');
      this.currentLevel = LOG_LEVELS.DEBUG;
      console.log('[Logger] Debug mode ENABLED - refresh to apply fully');
    }
  }
  
  disableDebug(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('debug');
      this.currentLevel = LOG_LEVELS.ERROR;
      console.log('[Logger] Debug mode DISABLED');
    }
  }

  private shouldLog(level: number): boolean {
    return level <= this.currentLevel;
  }

  private sanitizeData(data: any): any {
    if (typeof data === 'object' && data !== null) {
      const sanitized = { ...data };
      // Masquer les données sensibles (toujours actif, même sur Lovable)
      const sensitiveKeys = [
        'email', 'password', 'token', 'personalInfo',
        'accessToken', 'refreshToken', 'phone', 'address', 'secret', 'apiKey'
      ];
      sensitiveKeys.forEach(key => {
        if (key in sanitized) {
          sanitized[key] = '[PROTECTED]';
        }
      });
      return sanitized;
    }
    return data;
  }

  /**
   * ✅ Ajoute un log au buffer circulaire (50 entrées max)
   * Appelé par chaque méthode de log pour capturer le contexte avant un crash
   */
  addToBuffer(level: LogEntry['level'], message: string, data?: any): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      sessionId: SESSION_ID,
    };
    
    // Ajouter les données sanitizées (max 200 chars pour éviter les gros payloads)
    if (data) {
      try {
        entry.data = JSON.stringify(this.sanitizeData(data)).slice(0, 200);
      } catch {
        entry.data = '[Non-serializable]';
      }
    }
    
    logBuffer.push(entry);
    
    // Maintenir la taille du buffer (FIFO)
    if (logBuffer.length > LOG_BUFFER_SIZE) {
      logBuffer.shift();
    }
  }

  /**
   * ✅ Récupère les derniers logs pour les rapports d'erreur
   * Retourne une copie pour éviter les modifications externes
   */
  getRecentLogs(): LogEntry[] {
    return [...logBuffer];
  }

  /**
   * ✅ Vide le buffer (utile après un rapport d'erreur envoyé)
   */
  clearBuffer(): void {
    logBuffer.length = 0;
  }

  /**
   * ✅ Retourne le sessionId actuel (8 chars). Utile pour les rapports d'erreur.
   */
  getSessionId(): string {
    return SESSION_ID;
  }

  error(message: string, data?: any): void {
    // Toujours ajouter au buffer, même si le log n'est pas affiché
    this.addToBuffer('ERROR', message, data);
    
    if (!this.shouldLog(LOG_LEVELS.ERROR)) return;
    console.error(`[ERROR][sess:${SESSION_ID}] ${message}`, data ? this.sanitizeData(data) : '');
  }

  warn(message: string, data?: any): void {
    this.addToBuffer('WARN', message, data);
    
    if (!this.shouldLog(LOG_LEVELS.WARN)) return;
    console.warn(`[WARN][sess:${SESSION_ID}] ${message}`, data ? this.sanitizeData(data) : '');
  }

  info(message: string, data?: any): void {
    this.addToBuffer('INFO', message, data);
    
    if (!this.shouldLog(LOG_LEVELS.INFO)) return;
    console.info(`[INFO][sess:${SESSION_ID}] ${message}`, data ? this.sanitizeData(data) : '');
  }

  debug(message: string, data?: any): void {
    this.addToBuffer('DEBUG', message, data);
    
    if (!this.shouldLog(LOG_LEVELS.DEBUG)) return;
    console.log(`[DEBUG][sess:${SESSION_ID}] ${message}`, data ? this.sanitizeData(data) : '');
  }
}

export const logger = new Logger();
